import React, { PropsWithChildren, useMemo } from 'react';
import { EmailValidity } from '@/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { faSpinner, faCheck, faTimes } from '@fortawesome/pro-light-svg-icons';
import { Tooltip } from '@/components/Tooltip';
import theme from './ProjectInviteExternalEmailInput.module.scss';
import { classnames } from '@/utils/html';
import { ValidatingInput, ValidatingInputProps, ValidationRule } from '@ygtechnologies/bygr-frontend-component-library';

interface InputWrapperProps extends PropsWithChildren {
    emailValidity: EmailValidity;
    className?: string;
}

const InputWrapper: React.FC<InputWrapperProps> = ({ emailValidity, children, className }) => {
    return (
        <div className={classnames(className, theme.emailContainer)}>
            {children}
            <div className={theme.emailValidityIcon}>
                {(emailValidity === EmailValidity.BadEmail) && (
                    <FontAwesomeIcon className={theme.error} icon={faTimes} />
                )}
                {emailValidity === EmailValidity.Checking && <FontAwesomeIcon spin icon={faSpinner} />}
                {emailValidity === EmailValidity.Valid && (
                    <>
                        <Tooltip content={<Trans>Email is valid.</Trans>}>
                            <span>
                                <FontAwesomeIcon className={theme.success} icon={faCheck} />
                            </span>
                        </Tooltip>
                    </>
                )}
            </div>
        </div>
    );
};

type ProjectInviteExternalEmailInputProps = ValidatingInputProps & {
    emailValidity: EmailValidity;
};

const ProjectInviteExternalEmailInput: React.FC<ProjectInviteExternalEmailInputProps> = ({ emailValidity, ...rest }) => {
    const emailValidations: ValidationRule[] = useMemo(
        () => [
            {
                isValid: _ => emailValidity !== EmailValidity.AlreadyExists,
            },
            {
                isValid: (value) => emailValidity !== EmailValidity.BadEmail,
                summaryMessage: () => <Trans>No user with that email is registered at the selected company.</Trans>,
            },
        ],
        [emailValidity]
    );

    return (
        <InputWrapper emailValidity={emailValidity}>
            <ValidatingInput
                type="email"
                autoComplete="off"
                className="form-control"
                placeholder={t`Email`}
                validations={emailValidations}
                {...rest}
            />
        </InputWrapper>
    );
};

export default ProjectInviteExternalEmailInput;
